import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { StrategyTemplatePickerModal } from "@/components/StrategyTemplatePickerModal";
import {
  Buildings, UserCircle, Lightning, Check, ArrowRight,
  Sparkle, SpinnerGap,
} from "@phosphor-icons/react";

export const WelcomeStep = ({ user, isBeta, onboarding, businesses, onStart }) => (
  <div key="welcome" className="text-center space-y-5">
    <div className="w-16 h-16 mx-auto rounded-2xl bg-[var(--accent-subtle)] flex items-center justify-center">
      <Sparkle size={32} weight="fill" className="text-[#FF4400]" />
    </div>
    <div>
      <h2 className="font-['Cormorant_Garamond'] text-xl font-bold text-[var(--text-primary)]">
        Welcome to Quibn{user.name ? `, ${user.name.split(" ")[0]}` : ""}
      </h2>
      <p className="text-sm text-[var(--text-muted)] mt-2 max-w-sm mx-auto">
        Let's set up your workspace in under 2 minutes so every piece of content sounds like you.
      </p>
    </div>
    {isBeta && (
      <div className="bg-[var(--bg-base)] border border-[var(--border)] rounded-lg p-3">
        <p className="text-xs text-[var(--text-secondary)]">
          You're on the <span className="text-[#FF4400] font-semibold">Free Beta</span> plan. AI generation is included — no API key needed.
        </p>
      </div>
    )}
    <Button
      onClick={() => {
        if (onboarding?.has_strategy_profile) onStart(3);
        else if (onboarding?.has_business && businesses.length > 0) onStart(2);
        else onStart(1);
      }}
      className="bg-[#FF4400] hover:bg-[#FF6633] text-white gap-2 w-full"
      data-testid="onboarding-start-btn"
    >
      Get Started <ArrowRight size={16} />
    </Button>
  </div>
);

const LABEL_CLS = "block font-['JetBrains_Mono'] text-[10px] uppercase tracking-[0.2em] text-[var(--text-muted)] mb-1.5";

export const BusinessStep = ({ bizName, setBizName, bizIndustry, setBizIndustry, saving, onSubmit }) => (
  <div key="business" className="space-y-5">
    <div className="text-center">
      <div className="w-12 h-12 mx-auto rounded-xl bg-[var(--accent-subtle)] flex items-center justify-center mb-3">
        <Buildings size={24} weight="fill" className="text-[#FF4400]" />
      </div>
      <h2 className="font-['Cormorant_Garamond'] text-lg font-bold text-[var(--text-primary)]">Name Your Business</h2>
      <p className="text-xs text-[var(--text-muted)] mt-1">Each business gets its own brand voice, content library, and templates.</p>
    </div>
    <div className="space-y-3">
      <div>
        <label className={LABEL_CLS}>Business Name</label>
        <Input value={bizName} onChange={(e) => setBizName(e.target.value)} placeholder="e.g. ACME Consulting"
          className="bg-[var(--bg-base)] border-[var(--border-strong)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:ring-1 focus:ring-[#FF4400]" data-testid="onboarding-biz-name" />
      </div>
      <div>
        <label className={LABEL_CLS}>Industry (optional)</label>
        <Input value={bizIndustry} onChange={(e) => setBizIndustry(e.target.value)} placeholder="e.g. Marketing, SaaS, Coaching"
          className="bg-[var(--bg-base)] border-[var(--border-strong)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:ring-1 focus:ring-[#FF4400]" data-testid="onboarding-biz-industry" />
      </div>
    </div>
    <Button onClick={onSubmit} disabled={!bizName.trim() || saving}
      className="bg-[#FF4400] hover:bg-[#FF6633] text-white gap-2 w-full" data-testid="onboarding-create-biz-btn">
      {saving ? <SpinnerGap size={16} className="animate-spin" /> : <ArrowRight size={16} />}
      Create & Continue
    </Button>
  </div>
);

export const StrategyStep = ({ strategyName, setStrategyName, saving, onSubmit, showTemplatePicker, setShowTemplatePicker, onTemplateCloned }) => (
  <div key="strategy" className="space-y-5">
    <div className="text-center">
      <div className="w-12 h-12 mx-auto rounded-xl bg-[var(--accent-subtle)] flex items-center justify-center mb-3">
        <Lightning size={24} weight="fill" className="text-[#FF4400]" />
      </div>
      <h2 className="font-['Cormorant_Garamond'] text-lg font-bold text-[var(--text-primary)]">Create Your First Strategy Profile</h2>
      <p className="text-xs text-[var(--text-muted)] mt-1">Set your default strategic direction before ideating in chat.</p>
    </div>
    <div className="space-y-3">
      <Input value={strategyName} onChange={(e) => setStrategyName(e.target.value)} placeholder="e.g. B2B Authority Growth"
        className="bg-[var(--bg-base)] border-[var(--border-strong)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:ring-1 focus:ring-[#FF4400]" data-testid="onboarding-strategy-name" />
      <button type="button" onClick={() => setShowTemplatePicker(true)}
        className="text-xs text-[#FF4400] hover:text-[#FF6633] font-medium" data-testid="onboarding-strategy-template-btn">
        Or start from a template
      </button>
    </div>
    <Button onClick={onSubmit} disabled={saving}
      className="bg-[#FF4400] hover:bg-[#FF6633] text-white gap-2 w-full" data-testid="onboarding-create-strategy-btn">
      {saving ? <SpinnerGap size={16} className="animate-spin" /> : <ArrowRight size={16} />}
      Create & Continue
    </Button>
    <StrategyTemplatePickerModal isOpen={showTemplatePicker} onClose={() => setShowTemplatePicker(false)} onCloned={onTemplateCloned} />
  </div>
);

export const BrandVoiceStep = ({
  yourName, setYourName, yourTitle, setYourTitle, whoYouServe, setWhoYouServe,
  positioningStatement, setPositioningStatement, whatMakesDifferent, setWhatMakesDifferent,
  saving, onSubmit, onSkip,
}) => (
  <div key="brand" className="space-y-5">
    <div className="text-center">
      <div className="w-12 h-12 mx-auto rounded-xl bg-[var(--accent-subtle)] flex items-center justify-center mb-3">
        <UserCircle size={24} weight="fill" className="text-[#FF4400]" />
      </div>
      <h2 className="font-['Cormorant_Garamond'] text-lg font-bold text-[var(--text-primary)]">Quick Brand Voice</h2>
      <p className="text-xs text-[var(--text-muted)] mt-1">Tell the AI who you are. You can refine this later in Settings.</p>
    </div>
    <div className="space-y-3">
      <div>
        <label className={LABEL_CLS}>Your Name</label>
        <Input value={yourName} onChange={(e) => setYourName(e.target.value)} placeholder="Your full name"
          className="bg-[var(--bg-base)] border-[var(--border-strong)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:ring-1 focus:ring-[#FF4400]" data-testid="onboarding-your-name" />
      </div>
      <div>
        <label className={LABEL_CLS}>Title / Role</label>
        <Input value={yourTitle} onChange={(e) => setYourTitle(e.target.value)} placeholder="e.g. Marketing Consultant, Agency Owner, Freelance Strategist"
          className="bg-[var(--bg-base)] border-[var(--border-strong)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:ring-1 focus:ring-[#FF4400]" data-testid="onboarding-your-title" />
      </div>
      <div>
        <label className={LABEL_CLS}>Who You Serve</label>
        <textarea value={whoYouServe} onChange={(e) => setWhoYouServe(e.target.value)} placeholder="Describe your ideal audience..."
          className="w-full bg-[var(--bg-base)] border border-[var(--border-strong)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-1 focus:ring-[#FF4400] min-h-[60px] resize-y" data-testid="onboarding-who-you-serve" />
      </div>
      <div>
        <label className={LABEL_CLS}>Positioning Statement <span className="text-[var(--text-muted)] font-normal normal-case tracking-normal">(optional)</span></label>
        <Input value={positioningStatement} onChange={(e) => setPositioningStatement(e.target.value)} placeholder="e.g. I help B2B SaaS companies build demand gen engines"
          className="bg-[var(--bg-base)] border-[var(--border-strong)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:ring-1 focus:ring-[#FF4400]" data-testid="onboarding-positioning" />
      </div>
      <div>
        <label className={LABEL_CLS}>What Makes You Different? <span className="text-[var(--text-muted)] font-normal normal-case tracking-normal">(optional)</span></label>
        <Input value={whatMakesDifferent} onChange={(e) => setWhatMakesDifferent(e.target.value)} placeholder="e.g. 15 years in-house + proprietary framework"
          className="bg-[var(--bg-base)] border-[var(--border-strong)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:ring-1 focus:ring-[#FF4400]" data-testid="onboarding-differentiators" />
      </div>
    </div>
    <div className="flex gap-2">
      <Button onClick={onSkip} variant="outline"
        className="flex-1 border-[var(--border-strong)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]" data-testid="onboarding-skip-brand">
        Skip for now
      </Button>
      <Button onClick={onSubmit} disabled={saving}
        className="flex-1 bg-[#FF4400] hover:bg-[#FF6633] text-white gap-2" data-testid="onboarding-save-brand-btn">
        {saving ? <SpinnerGap size={16} className="animate-spin" /> : <ArrowRight size={16} />}
        Save & Continue
      </Button>
    </div>
  </div>
);

export const ReadyStep = ({ onboarding, businesses, yourName, isBeta, onFinish }) => (
  <div key="ready" className="text-center space-y-5">
    <div className="w-16 h-16 mx-auto rounded-2xl bg-[var(--success)]/10 flex items-center justify-center">
      <Check size={32} weight="bold" className="text-[var(--success)]" />
    </div>
    <div>
      <h2 className="font-['Cormorant_Garamond'] text-xl font-bold text-[var(--text-primary)]">You're All Set</h2>
      <p className="text-sm text-[var(--text-muted)] mt-2">
        Your workspace is ready. Start in guided brainstorm chat to create your first brief.
      </p>
    </div>
    <div className="bg-[var(--bg-base)] border border-[var(--border)] rounded-lg p-4 text-left space-y-2">
      <CheckItem done={onboarding?.has_business || businesses.length > 0} label="Business created" />
      <CheckItem done={onboarding?.has_strategy_profile} label="Strategy profile created" />
      <CheckItem done={!!yourName || onboarding?.has_business_profile} label="Brand voice configured" />
      {!isBeta && <CheckItem done={onboarding?.has_api_key} label="API key added" />}
    </div>
    <Button onClick={onFinish} className="bg-[#FF4400] hover:bg-[#FF6633] text-white gap-2 w-full" data-testid="onboarding-finish-btn">
      <Lightning size={16} weight="fill" /> Start Guided Brainstorm
    </Button>
  </div>
);

function CheckItem({ done, label }) {
  return (
    <div className="flex items-center gap-2.5">
      <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
        done ? "bg-[var(--success)]/20 text-[var(--success)]" : "bg-[var(--bg-raised)] text-[var(--text-muted)]"
      }`}>
        {done ? <Check size={12} weight="bold" /> : <div className="w-2 h-2 rounded-full bg-[var(--text-muted)]/40" />}
      </div>
      <span className={`text-sm ${done ? "text-[var(--text-secondary)]" : "text-[var(--text-muted)]"}`}>{label}</span>
    </div>
  );
}
